import entries from 'lodash/entries';
import first from 'lodash/first';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isPlainObject from 'lodash/isPlainObject';
import keys from 'lodash/keys';
import values from 'lodash/values';
import zipObject from 'lodash/zipObject';
import { requiredArg } from './args';
import { DetailedError } from './error';

export const formNameFromKeyPath = keyPath => {
  if (!isPlainObject(keyPath))
    throw new Error('keyPath must be a plain object.');

  return entries(keyPath).reduce((acc, [key, value]) => {
    if (/^sectionName(?:\d+)?$/.test(key) || !value) return acc;
    return [...acc, value];
  }, []);
};

export const localizationKeyPath = keyPath => {
  if (!isPlainObject(keyPath))
    throw new Error('keyPath must be a plain object.');

  return entries(keyPath).reduce((acc, [key, value]) => {
    if (/^index(?:\d+)?$/.test(key) || !value) return acc;
    return [...acc, value];
  }, []);
};

export const getFieldName = context =>
  formNameFromKeyPath(context.keyPath).join('.');

export const getTranslationScopePath = context =>
  localizationKeyPath(context.keyPath).join('.');

export const translationsForKeyPath = ({ keyPath, translations }) =>
  get(translations, localizationKeyPath(keyPath));

export const getSchemaAtCursor = (
  schemaPath = requiredArg('schemaPath'),
  schema = requiredArg('schema')
) => get(schema, schemaPath);

export const extendKeyPath = (keyPath, segment) => {
  const getUniqueName = (name, increment = 2) => {
    if (!(name in keyPath)) return name;
    if (!(name + increment in keyPath)) return name + increment;
    return getUniqueName(name, increment + 1);
  };

  const uniqueNames = keys(segment).map(name => getUniqueName(name));
  return { ...keyPath, ...zipObject(uniqueNames, values(segment)) };
};

export const matchDependantPropertyName = (activeDependencies, propertyName) =>
  activeDependencies.find(({ dependentPropertyNames }) =>
    includes(dependentPropertyNames, propertyName)
  );

export const getPageLevelSchemaPath = ({
  propertyName,
  activeDependencies = [],
  schemaPath = [],
}) => {
  const DetailedErrorMessage = DetailedError('PageLevelSchemaPath');

  const dependency = matchDependantPropertyName(
    activeDependencies,
    propertyName
  );

  if (dependency) {
    switch (dependency.keyword) {
      case 'then':

      case 'else':
        return [dependency.keyword, ...schemaPath, 'properties', propertyName];

      case 'allOf.then':

      case 'allOf.else': {
        const [, keyword] = dependency.keyword.split('.');
        const [, ...parentPath] = first(dependency.parentDependencies);

        return [
          ...parentPath,
          keyword,
          ...schemaPath,
          'properties',
          propertyName,
        ];
      }

      default: {
        throw new DetailedErrorMessage(
          `${dependency.keyword} keyword is unknown`
        );
      }
    }
  }

  throw new DetailedErrorMessage(
    `No page level dependency found for ${propertyName}`
  );
};

export const getSchemaPath = ({
  propertyName,
  activeDependencies = [],
  schemaPath = [],
}) => {
  const dependency = matchDependantPropertyName(
    activeDependencies,
    propertyName
  );

  if (dependency) {
    switch (dependency.keyword) {
      case 'then':

      case 'else': {
        const [, ...parentPath] = first(dependency.parentDependencies);

        return [
          ...schemaPath,
          ...parentPath,
          dependency.keyword,
          'properties',
          propertyName,
        ];
      }

      case 'allOf.properties': {
        const [, ...parentPath] = first(dependency.parentDependencies);

        return [...schemaPath, ...parentPath, 'properties', propertyName];
      }

      case 'allOf.then':

      case 'allOf.else': {
        const [, keyword] = dependency.keyword.split('.');
        const [, ...parentPath] = first(dependency.parentDependencies);

        return [
          ...schemaPath,
          ...parentPath,
          keyword,
          'properties',
          propertyName,
        ];
      }
    }
  } else {
    return [...schemaPath, 'properties', propertyName];
  }
};
